import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import { Search, Plus, Calendar, MessageCircle } from 'lucide-react';
import { getTimeSlots, getTagColor } from '../utils/helpers';
import { RewardUpsellBanner } from './RewardUpsellBanner';

const CreateAppointmentModal = ({ isOpen, onClose, onSuccess, onCreate, selectedDate, user, initialPatient }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [newPatientName, setNewPatientName] = useState('');
  const [newPatientCountryCode, setNewPatientCountryCode] = useState('+91');
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  // Format date in local timezone to avoid UTC conversion issues
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const [appointmentDate, setAppointmentDate] = useState(formatLocalDate(selectedDate));
  const [appointmentTime, setAppointmentTime] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  // Close service dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#service') && !event.target.closest('.service-dropdown')) {
        setShowServiceDropdown(false);
      }
    };

    if (showServiceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showServiceDropdown]);

  // Update appointment date when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      setAppointmentDate(formatLocalDate(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    // Check for pre-filled patient data when modal opens
    if (isOpen) {
      if (initialPatient) {
        setPhone(initialPatient.primary_phone || '');
        setSelectedPatient(initialPatient);
        setStep(4);
        return;
      }
      const prefilledData = localStorage.getItem('prefilledPatient');
      if (prefilledData) {
        try {
          const patient = JSON.parse(prefilledData);
          setPhone(patient.phone);
          setSelectedPatient({
            id: patient.id,
            name: patient.name,
            primary_phone: patient.phone
          });
          setStep(4); // Go directly to service selection
          // Clear the prefilled data
          localStorage.removeItem('prefilledPatient');
          toast.success(`Booking for ${patient.name}`);
        } catch (e) {
          console.error('Error parsing prefilled patient data:', e);
        }
      }
    } else {
      // Reset form when modal closes
      setStep(1);
      setPhone('');
      setPatients([]);
      setSelectedPatient(null);
      setNewPatientName('');
      setNewPatientCountryCode('+91');
      setSelectedService('');
      setServiceSearchQuery('');
      setShowServiceDropdown(false);
      setAppointmentDate(formatLocalDate(selectedDate));
      setAppointmentTime('');
    }
  }, [isOpen, selectedDate, initialPatient]);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      const activeServices = response.data.filter(s => s.status === 'active');
      // Sort services alphabetically by name
      const sortedServices = activeServices.sort((a, b) => a.name.localeCompare(b.name));
      setServices(sortedServices);
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const handleSearchPhone = async () => {
    if (phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const response = await api.get(`/patients/by-phone/${phone}`);
      setPatients(response.data);
      if (response.data.length === 0) {
        setStep(2); // New patient
      } else {
        setStep(3); // Select existing patient
      }
    } catch (error) {
      toast.error('Failed to search patients');
    }
  };

  const handleCreatePatient = async () => {
    if (!newPatientName) {
      toast.error('Please enter patient name');
      return;
    }

    try {
      const response = await api.post('/patients', {
        name: newPatientName,
        primary_phone: phone,
        country_code: newPatientCountryCode,
        additional_phone_numbers: [],
        tags: []
      });
      setSelectedPatient(response.data);
      setStep(4);
    } catch (error) {
      toast.error('Failed to create patient');
    }
  };

  const handleSendRegistrationLink = async () => {
    setSendingInvite(true);
    try {
      const res = await api.post('/registrations/invite', { phone, country_code: newPatientCountryCode });
      if (res.data?.wa_success === false) {
        toast.error(`WhatsApp send failed: ${res.data.wa_error || 'unknown error'}. The number is saved under New Registrations — use Resend once the template is approved.`);
      } else {
        toast.success(`Registration link sent on WhatsApp to ${phone}. They'll appear under New Registrations once they fill it.`);
      }
      onClose();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send registration link');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setStep(4);
  };

  const handleCreateAppointment = () => {
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    const service = services.find(s => s.id === selectedService);
    if (service.type === 'time_based' && !appointmentTime) {
      toast.error('Please select a time for time-based appointment');
      return;
    }

    // Prepare data and create appointment directly
    const appointmentData = {
      patient_id: selectedPatient.id,
      service_id: selectedService,
      scheduled_date: appointmentDate,
      scheduled_time: service.type === 'time_based' ? appointmentTime : null
    };

    onCreate(appointmentData);
  };

  const resetForm = () => {
    setStep(1);
    setPhone('');
    setPatients([]);
    setSelectedPatient(null);
    setNewPatientName('');
    setNewPatientCountryCode('+91');
    setSelectedService('');
    setServiceSearchQuery('');
    setShowServiceDropdown(false);
    setAppointmentTime('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
        resetForm();
      }
    }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-purple-600" />
            Create New Appointment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone">Patient Phone Number</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="phone"
                    data-testid="appointment-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                    className="flex-1"
                  />
                  <Button onClick={handleSearchPhone} data-testid="search-phone-button">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Create New Patient */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-sky-50 rounded-lg">
                <p className="text-sm text-sky-800">
                  No patient found with phone number <strong>{phone}</strong>. Create a new patient record.
                </p>
              </div>
              <div>
                <Label htmlFor="patientName">Patient Name</Label>
                <Input
                  id="patientName"
                  data-testid="new-patient-name-input"
                  type="text"
                  value={newPatientName}
                  onChange={(e) => setNewPatientName(e.target.value)}
                  placeholder="Enter patient name"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="countryCode">Country Code</Label>
                <select
                  id="countryCode"
                  data-testid="country-code-select"
                  value={newPatientCountryCode}
                  onChange={(e) => setNewPatientCountryCode(e.target.value)}
                  className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="+91">+91 (India)</option>
                  <option value="+1">+1 (USA/Canada)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+61">+61 (Australia)</option>
                  <option value="+971">+971 (UAE)</option>
                  <option value="+65">+65 (Singapore)</option>
                  <option value="+60">+60 (Malaysia)</option>
                  <option value="+82">+82 (South Korea)</option>
                  <option value="+81">+81 (Japan)</option>
                  <option value="+86">+86 (China)</option>
                  <option value="+49">+49 (Germany)</option>
                  <option value="+33">+33 (France)</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={handleCreatePatient} data-testid="create-patient-button" className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Patient
                </Button>
              </div>
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-gray-400 uppercase tracking-wider">or let the patient register</span></div>
              </div>
              <Button
                variant="outline"
                onClick={handleSendRegistrationLink}
                disabled={sendingInvite}
                data-testid="send-registration-link-button"
                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {sendingInvite ? 'Sending…' : 'Send registration link on WhatsApp'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                A welcome message with a secure form link (valid 7 days) is sent. Registered patients show up in the <strong>New Registrations</strong> box on the dashboard.
              </p>
            </div>
          )}

          {/* Step 3: Select Existing Patient */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 rounded-lg">
                <p className="text-sm text-emerald-800">
                  Found {patients.length} patient(s) with phone number <strong>{phone}</strong>
                </p>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {patients.map(patient => (
                  <div
                    key={patient.id}
                    data-testid={`select-patient-${patient.id}`}
                    onClick={() => handleSelectPatient(patient)}
                    className="p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition"
                  >
                    <div className="font-semibold text-gray-900">{patient.name}</div>
                    <div className="text-sm text-gray-600">{patient.primary_phone}</div>
                    {patient.tags && patient.tags.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {patient.tags.map(tag => (
                          <Badge key={tag} className={`${getTagColor(tag)} text-xs`}>
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Patient Instead
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Select Service & Time */}
          {step === 4 && selectedPatient && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-800">
                  Creating appointment for <strong>{selectedPatient.name}</strong>
                </p>
              </div>

              {selectedPatient.id && <RewardUpsellBanner patientId={selectedPatient.id} />}

              <div className="relative">
                <Label htmlFor="service">Service</Label>
                <div className="relative mt-2">
                  <Input
                    id="service"
                    type="text"
                    placeholder="Search and select a service..."
                    value={
                      selectedService
                        ? services.find(s => s.id === selectedService)?.name || ''
                        : serviceSearchQuery
                    }
                    onChange={(e) => {
                      setServiceSearchQuery(e.target.value);
                      setSelectedService('');
                      setShowServiceDropdown(true);
                    }}
                    onFocus={() => setShowServiceDropdown(true)}
                    className="w-full"
                  />
                  {showServiceDropdown && (
                    <div className="service-dropdown absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded-md shadow-lg">
                      {services
                        .filter(service =>
                          service.name.toLowerCase().includes(serviceSearchQuery.toLowerCase())
                        )
                        .map(service => (
                          <div
                            key={service.id}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                              setSelectedService(service.id);
                              setServiceSearchQuery('');
                              setShowServiceDropdown(false);
                            }}
                          >
                            <div className="font-medium">{service.name}</div>
                            <div className="text-xs text-gray-500">
                              {service.type === 'time_based' ? 'Time-based' : 'Queue-based'}
                            </div>
                          </div>
                        ))}
                      {services.filter(service =>
                        service.name.toLowerCase().includes(serviceSearchQuery.toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          No services found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedService && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedService('');
                      setServiceSearchQuery('');
                    }}
                    className="absolute right-2 top-[38px] text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>

              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="appointment-date-input"
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  onDoubleClick={(e) => e.stopPropagation()}
                  min={formatLocalDate(new Date())}
                  className="mt-2"
                />
              </div>

              {selectedService && services.find(s => s.id === selectedService)?.type === 'time_based' && (
                <div>
                  <Label htmlFor="time">Time</Label>
                  <select
                    id="time"
                    data-testid="appointment-time-select"
                    value={appointmentTime}
                    onChange={(e) => setAppointmentTime(e.target.value)}
                    className="mt-2 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select a time</option>
                    {getTimeSlots().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setStep(3)}>Back</Button>
                <Button 
                  onClick={handleCreateAppointment} 
                  data-testid="create-appointment-submit-button"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {loading ? 'Creating...' : 'Create Appointment'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAppointmentModal;
